// A run of bars written as a step sequencer reads it: each voice is a row of sixteenths per bar,
// and a voice's rows cycle through the section's bars.
export type Section = {
  readonly name: string;
  readonly bars: number;
  // "x" is a hit and "." a rest, in every row.
  readonly kick: ReadonlyArray<string>;
  readonly clap: ReadonlyArray<string>;
  // "x" closed, "o" open.
  readonly hat: ReadonlyArray<string>;
  // "x" the bar's chord root, "^" that root an octave up.
  readonly bass: ReadonlyArray<string>;
  // A digit is a degree of the major scale: 1 the key's root, 5 its fifth, 8 its octave.
  readonly lead: ReadonlyArray<string>;
};

export type Song = {
  readonly bpm: number;
  // Chord roots as scale degrees, one per bar, restarting at each section's first bar.
  readonly progression: ReadonlyArray<number>;
  readonly sections: ReadonlyArray<Section>;
};

// Sixteenths in a bar of four.
const STEPS = 16;

export type Note =
  | { readonly voice: "kick"; readonly at: number }
  | { readonly voice: "clap"; readonly at: number }
  | { readonly voice: "hat"; readonly at: number; readonly open: boolean }
  | { readonly voice: "bass"; readonly at: number; readonly hz: number }
  | { readonly voice: "lead"; readonly at: number; readonly hz: number };

export type Voice = Note["voice"];

const REST = ["................"];
const FOUR = ["x...x...x...x..."];
const BACKBEAT = ["....x.......x..."];
const OFFBEAT = [".x.x.x.x.x.x.x.x"];

// The one song, placed on server time so a note's moment is the same on every machine. 120 BPM
// in F major, after Miss Monique's "Tran": the kick on every beat, hats on the eighths and a bass
// that answers each kick on the offbeat, which is the bounce. The chords walk I V vi IV. Seventy-
// two seconds: the intro withholds everything but the kick, the groove brings the bounce in, the
// build lays a riff over driving hats, the lift rolls the claps while the rest drops out, and the
// drop lands with every voice for half the loop.
export const SONG: Song = {
  bpm: 120,
  progression: [1, 5, 6, 4],
  sections: [
    { name: "intro", bars: 4, kick: FOUR, clap: REST, hat: REST, bass: REST, lead: REST },
    {
      name: "groove",
      bars: 8,
      kick: FOUR,
      clap: BACKBEAT,
      hat: ["..x...x...x...x."],
      bass: OFFBEAT,
      lead: REST,
    },
    {
      name: "build",
      bars: 6,
      kick: FOUR,
      clap: BACKBEAT,
      hat: ["xxxxxxxxxxxxxxxx"],
      bass: OFFBEAT,
      lead: ["1.3.5.8.5.3.1.3.", "5.7.9.7.5.2.5.7.", "6.8.3.8.6.8.9.8.", "4.6.8.6.4.6.8.9."],
    },
    {
      name: "lift",
      bars: 2,
      kick: ["x...x...x...x...", "x...x..........."],
      clap: ["x.x.x.x.x.x.x.x.", "xxxxxxxxxxxxxxxx"],
      hat: ["xxxxxxxxxxxxxxxx", "xxxxxxxx........"],
      bass: [".x.x.x.x.x.x.x.x", ".x.x.x.x........"],
      lead: REST,
    },
    {
      name: "drop",
      bars: 16,
      kick: FOUR,
      clap: BACKBEAT,
      hat: ["x.o.x.o.x.o.x.o."],
      bass: [".x.x.x.x.x.x.^.x"],
      lead: ["8..5..3.8..5..3.", "9..7..5.9..7..5.", "8..6..3.8..6..3.", "8..6..4.8..6..9."],
    },
  ],
};

// The bass sits on F2 and the lead on F4; a degree of the major scale climbs from there.
const BASS_HZ = 87.31;
const LEAD_HZ = 349.23;
const MAJOR = [0, 2, 4, 5, 7, 9, 11];

const pitch = (baseHz: number, degree: number): number => {
  const semitones = MAJOR[(degree - 1) % 7] + 12 * Math.floor((degree - 1) / 7);
  return baseHz * 2 ** (semitones / 12);
};

// Four beats, in milliseconds; the chart's measures are this long too.
export const barMs = (song: Song): number => (60_000 / song.bpm) * 4;

const barsOf = (song: Song): number =>
  song.sections.reduce((total, section) => total + section.bars, 0);

const lengthMs = (song: Song): number => barMs(song) * barsOf(song);

// The section a bar of the loop falls in, and the bar the section starts on.
const sectionAt = (
  song: Song,
  bar: number,
): { readonly section: Section; readonly start: number } => {
  let index = 0;
  let start = 0;
  while (bar >= start + song.sections[index].bars) {
    start += song.sections[index].bars;
    index++;
  }
  return { section: song.sections[index], start };
};

// Every note whose moment is in [fromMs, toMs), oldest first; a bar's voices sound in the order
// kick, clap, hat, bass, lead. Times are absolute server time; the song repeats every
// `lengthMs`, and a time before the epoch is the same point in the loop as `timeMs + lengthMs`.
export const notesBetween = (song: Song, fromMs: number, toMs: number): ReadonlyArray<Note> => {
  const bar = barMs(song);
  const step = bar / STEPS;
  const bars = barsOf(song);
  const out: Array<Note> = [];
  for (let index = Math.floor(fromMs / bar); index * bar < toMs; index++) {
    const inLoop = ((index % bars) + bars) % bars;
    const { section, start } = sectionAt(song, inLoop);
    const within = inLoop - start;
    const root = song.progression[within % song.progression.length];
    const kick = section.kick[within % section.kick.length];
    const clap = section.clap[within % section.clap.length];
    const hat = section.hat[within % section.hat.length];
    const bass = section.bass[within % section.bass.length];
    const lead = section.lead[within % section.lead.length];
    for (let i = 0; i < STEPS; i++) {
      const at = index * bar + i * step;
      if (at < fromMs || at >= toMs) {
        continue;
      }
      if (kick[i] === "x") {
        out.push({ voice: "kick", at });
      }
      if (clap[i] === "x") {
        out.push({ voice: "clap", at });
      }
      if (hat[i] === "x" || hat[i] === "o") {
        out.push({ voice: "hat", at, open: hat[i] === "o" });
      }
      if (bass[i] === "x" || bass[i] === "^") {
        out.push({ voice: "bass", at, hz: pitch(BASS_HZ, bass[i] === "^" ? root + 7 : root) });
      }
      const degree = lead.charCodeAt(i) - 48;
      if (degree >= 1 && degree <= 9) {
        out.push({ voice: "lead", at, hz: pitch(LEAD_HZ, degree) });
      }
    }
  }
  return out;
};

// Where in the song a moment falls: the section's name, the bar of the loop (from 0), the beat
// of the bar (0 to 3) and how far through that beat it is (0 up to 1).
export type Place = {
  readonly section: string;
  readonly bar: number;
  readonly beat: number;
  readonly phase: number;
};

export const placeAt = (song: Song, timeMs: number): Place => {
  const length = lengthMs(song);
  const bar = barMs(song);
  const beat = bar / 4;
  const inLoop = ((timeMs % length) + length) % length;
  const barIndex = Math.floor(inLoop / bar);
  const inBar = inLoop - barIndex * bar;
  const beatIndex = Math.floor(inBar / beat);
  return {
    section: sectionAt(song, barIndex).section.name,
    bar: barIndex,
    beat: beatIndex,
    phase: (inBar - beatIndex * beat) / beat,
  };
};
